import { QueryClient, useQuery } from "@tanstack/react-query";
import axios from "axios";

import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";

import { useGetProduct } from "./product-queries";

/**
 * Website query keys.
 *
 * These uniquely identify queries in the `react-query` cache.
 *
 * [More on this pattern](https://tkdodo.eu/blog/effective-react-query-keys)
 */
export const websiteKeys = {
  csrfToken: [{ scope: "website", data: "csrfToken" }] as const,
};

/**
 * Set defaults for product queries
 *
 * Setting these here applies them globally whether called directly or via custom hooks
 *
 * Also helpful for testing as they can be easily overriden
 * (e.g. to stop retries from slowing tests down)
 */
export function setWebsiteQueryDefaults(queryClient: QueryClient) {
  /**
   * Never consider the csrfToken to be stale; it won't be refetched
   */
  queryClient.setQueryDefaults([{ data: "csrfToken" }], {
    staleTime: Infinity,
    cacheTime: Infinity,
  });
}

/**
 * Essential token for proxy requests.
 *
 * Usually executed as a `<script>` to place value on the `window`,
 * but this creates a race condition for queries that depend on it.
 */
export function useGetCsrfToken() {
  const { data: product } = useGetProduct();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  return useQuery({
    queryKey: websiteKeys.csrfToken,
    queryFn: async () => {
      // Returns a string like `window._csrf='...'`
      const response = await axios.get<string>("/website-api/v1/csrf-token");

      // Get the CSRF token from inside the quotes
      return response.data.match(/'(.*?)'/)?.[1];
    },
  });
}
